import axios from 'axios'
import { log } from 'src/services/app/log.service'
import { API_ERRORS, AUTH_ENDPOINTS, ERROR_TYPES } from 'src/services/consts/api.const'
import { REMOVE_ACCOUNT_MEMBER_ERROR_CODE } from 'src/services/consts/account-members.const'
import { notifyMsgs, notifyService } from 'src/services/app/notify.service'
import { router } from 'src/router'
import { ROUTES } from 'src/router/const'
import { useAuthStore } from 'stores/auth'
import { useAccountStore } from 'stores/account'
import { resetStores } from 'stores/reset.store'
import { usePostHog } from 'src/composables/app/ui/usePostHog'
import { getResponseErrorCode } from 'src/services/helpers/auth.helper'
import { sanitizePayload } from 'src/services/helpers/security.helper'
import { formatApiErrorDetail } from 'src/services/helpers/util.helper'
import { apiUrl } from 'src/config'

let _authErrorInProgress = false
const { captureException } = usePostHog()

const httpClient = axios.create({
  timeout: 30000,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - handles token refresh + tenant injection before requests
httpClient.interceptors.request.use(
  async (config) => {
    const isTrustedDomain = _isTrustedApiDomain(config.url)

    // Only add auth headers and account data to trusted internal API domains
    if (!isTrustedDomain) {
      return config
    }

    const isFormData = config.data instanceof FormData
    const accountStore = useAccountStore()
    const authStore = useAuthStore()

    if (authStore.isAuthenticated) {
      try {
        const token = await authStore.getAccessToken()
        config.headers.Authorization = `Bearer ${token}`
      } catch (err) {
        log.error('Token refresh failed - session expired', err)
        const error = new Error('Session expired - token refresh failed')
        error.name = API_ERRORS.AUTH_EXPIRED
        error.config = config
        throw error
      }
    }

    if (isFormData) {
      if (accountStore.accountId) {
        config.data.set('account_id', accountStore.accountId)
      }
      delete config.headers['Content-Type']
    } else if (config.data && accountStore.accountId) {
      config.data.account_id = accountStore.accountId
    }

    return config
  },
  (error) => {
    log.error('[HTTP SERVICE] - request interceptor - request error', error)
    return Promise.reject(error)
  }
)

// Response interceptor
httpClient.interceptors.response.use(
  (response) => {
    const isFormData = response.config?.data instanceof FormData
    const url = response.config?.url || ''
    const endpoint = url.split('/').pop() || url
    const sanitizedRequest = isFormData ? '[FormData]' : sanitizePayload(response.config?.data)
    const sanitizedResponse = sanitizePayload(response.data)
    log.info(`API: ${endpoint}`, {
      url,
      method: response.config?.method,
      request: sanitizedRequest,
      response: sanitizedResponse,
    })

    // Check for error field in successful responses
    if (response.data && response.data.error) {
      if (response.config?.acceptErrorPayload) {
        return response.data
      }
      const error = new Error(formatApiErrorDetail(response.data.error))
      error.response = response
      error.config = response.config
      _captureApiError(error)
      return Promise.reject(error)
    }

    return response.data
  },
  async (error) => {
    _captureApiError(error)

    if (error.name === API_ERRORS.AUTH_EXPIRED) {
      _handleAuthError(true)
      return Promise.reject(error)
    }

    const { response, config } = error

    if (response) {
      const isMeBootstrap = _isMeBootstrapRequest(config?.url)

      switch (response.status) {
        case 401:
          // GET /me 401 stays on AuthErrorPanel (including trial_expired).
          // Any other 401 — trial_expired included — logs out; the next login
          // hits /me and the gate already knows how to show trial copy.
          if (!isMeBootstrap) {
            const shouldShowSessionExpired =
              response.data?.error === API_ERRORS.UNAUTHORIZED ||
              response.data?.error === API_ERRORS.TRIAL_EXPIRED
            _handleAuthError(shouldShowSessionExpired)
          }
          break

        case 404:
          // ME bootstrap 404 = Auth0 OK but not provisioned — surfaced on AuthErrorPanel.
          break

        case 403:
          if (
            getResponseErrorCode(response) === REMOVE_ACCOUNT_MEMBER_ERROR_CODE.CANNOT_TARGET_SELF
          ) {
            break
          }
          notifyService.error(notifyMsgs.unauthorized, 'permission-error')
          router.push({ name: ROUTES.OVERVIEW })
          break

        default:
          break
      }
    }

    return Promise.reject(error)
  }
)

// API methods
export const httpService = {
  get: (url, config = {}) => httpClient.get(url, config),
  post: (url, data = {}, config = {}) => httpClient.post(url, data, config),
  put: (url, data = {}, config = {}) => httpClient.put(url, data, config),
  delete: (url, config = {}) => httpClient.delete(url, config),
  patch: (url, data = {}, config = {}) => httpClient.patch(url, data, config),
}

// ==================== PRIVATE HELPERS ====================

async function _handleAuthError(showSessionExpired = false) {
  if (_authErrorInProgress) return
  _authErrorInProgress = true

  const authStore = useAuthStore()
  try {
    await authStore.logout({ authError: true })
  } catch (err) {
    log.error('Failed to logout properly', err)
    resetStores()
    router.push({ path: '/not-found' })
  } finally {
    _authErrorInProgress = false
  }

  if (showSessionExpired) {
    notifyService.error(notifyMsgs.sessionExpired, 'auth-error')
  }
}

/**
 * Capture API error to PostHog with readable, structured names.
 *
 * Skipped when the browser is offline (navigator.onLine === false) —
 * user-side connectivity issues, not server problems.
 * See also: _setupOnlineManager in vue-query.js verifies connectivity before refetches.
 *
 * PostHog UI title  = apiError.name: apiError.message  (from the Error object)
 * Slack properties  = error_name, original_error_message, error_context  (custom props)
 *
 * Error message format uses bracket prefixes for at-a-glance context:
 *   [Auth]           - No ID token, session expired before request
 *   [status]         - Server responded with an error (e.g. [401], [500])
 *   [error.code]     - No response received, bracket shows native Axios error code:
 *                       ECONNABORTED (timeout), ERR_NETWORK (CORS/offline/DNS),
 *                       ERR_CANCELED (aborted), etc.
 *   [Network Error]  - No response and no error code (unknown failure)
 */
function _captureApiError(error) {
  const { response, config } = error
  const method = config?.method?.toUpperCase() || 'UNKNOWN'
  const url = config?.url || 'unknown-endpoint'

  if (!navigator.onLine) return

  const isMeBootstrap = _isMeBootstrapRequest(url)
  const isAuth =
    error.name === API_ERRORS.AUTH_EXPIRED ||
    response?.status === 401 ||
    (isMeBootstrap && response?.status === 404)
  let errorMessage

  const responseErrorCode = getResponseErrorCode(response)

  if (error.name === API_ERRORS.AUTH_EXPIRED) {
    errorMessage = `[${API_ERRORS.AUTH_EXPIRED}] No ID token available [${method} ${url}]`
  } else if (response) {
    const detail = formatApiErrorDetail(response.data?.error, response.statusText || 'Server error')
    errorMessage = `[${API_ERRORS.REQ_ERROR}] ${method} ${url} - ${detail} (${response.status})`
  } else {
    const label = error.code || API_ERRORS.NETWORK_ERROR
    errorMessage = `[${label}] ${method} ${url} - No response received`
  }

  captureException(error, {
    error_type: isAuth ? ERROR_TYPES.AUTH : ERROR_TYPES.API,
    error_name: errorMessage,
    original_error_message: error.message,
    error_context: `${method} ${url}`,
    request_url: config?.url,
    request_method: config?.method,
    request_payload: sanitizePayload(config?.data),
    error_code: error.code,
    ...(responseErrorCode && { response_error_code: responseErrorCode }),
    response_status: response?.status,
    error_data: sanitizePayload(response?.data),
  })
}

function _isMeBootstrapRequest(url) {
  if (!url || !AUTH_ENDPOINTS.ME) return false

  if (url === AUTH_ENDPOINTS.ME || url.endsWith('/me')) return true

  try {
    return new URL(url).pathname.endsWith('/me')
  } catch {
    return false
  }
}

/**
 * Check if URL belongs to a trusted internal API domain.
 * Prevents leaking auth headers to external/third-party services.
 */
function _isTrustedApiDomain(url) {
  if (!url) return false

  // Relative URLs are trusted (same origin), but exclude protocol-relative URLs (//evil.com)
  if (url.startsWith('/') && !url.startsWith('//')) return true

  try {
    const TRUSTED_API_ORIGINS = [apiUrl].filter(Boolean).map((url) => {
      try {
        return new URL(url).origin
      } catch {
        return url
      }
    })
    const requestOrigin = new URL(url).origin
    return TRUSTED_API_ORIGINS.includes(requestOrigin)
  } catch {
    // If URL parsing fails, treat as untrusted
    return false
  }
}
